import React from 'react';
import ReactDOM from 'react-dom';
import { Domain } from '../services/api';
import { CheckCircle, XCircle, Eye, EyeOff, Loader2, Plus, Shield, Search, MoreVertical, UserPlus, UserMinus, Copy } from 'lucide-react';
import apiService from '../services/api';

interface DomainsTableProps {
  domains: Domain[];
  loading: boolean;
  error: string | null;
}

export const DomainsTable: React.FC<DomainsTableProps> = ({ domains, loading, error }) => {
  const [showPasswords, setShowPasswords] = React.useState<Set<string>>(new Set());
  const [expandedResults, setExpandedResults] = React.useState<Set<string>>(new Set());
  const [copiedOtpFor, setCopiedOtpFor] = React.useState<string | null>(null);
  const [cloudflareStatuses, setCloudflareStatuses] = React.useState<Record<string, string>>({});
  const [loadingStatuses, setLoadingStatuses] = React.useState<Set<string>>(new Set());
  const [dkimStatuses, setDkimStatuses] = React.useState<Record<string, boolean>>({});
  const [googleVerificationStatuses, setGoogleVerificationStatuses] = React.useState<Record<string, boolean>>({});
  
  // Modal states for DNS record actions
  const [showDkimModal, setShowDkimModal] = React.useState(false);
  const [showGoogleVerificationModal, setShowGoogleVerificationModal] = React.useState(false);
  const [showActionsModal, setShowActionsModal] = React.useState(false);
  const [selectedDomain, setSelectedDomain] = React.useState<Domain | null>(null);
  const [dkimContent, setDkimContent] = React.useState('');
  const [googleVerificationContent, setGoogleVerificationContent] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState<Record<string, boolean>>({});
  const [domainVerificationLoading, setDomainVerificationLoading] = React.useState<Set<string>>(new Set());

  // Domain claiming states
  const [claimingDomains, setClaimingDomains] = React.useState<Set<string>>(new Set());
  const [managerNames, setManagerNames] = React.useState<Record<string, string>>({});

  // Bulk selection states
  const [selectedDomains, setSelectedDomains] = React.useState<Set<string>>(new Set());
  const [bulkClaimingInProgress, setBulkClaimingInProgress] = React.useState(false);

  // Get current user (for claiming functionality)
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('alphoric_auth');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user;
      }
    } catch (error) {
      console.error('Error getting current user:', error);
    }
    return null;
  };

  const currentUser = getCurrentUser();

  // Helper function to check if domain was created after June 1st, 2025
  const isCreatedAfterJune2025 = (domain: Domain): boolean => {
    if (!domain.created_at) return false;
    const createdDate = new Date(domain.created_at);
    const cutoffDate = new Date('2025-06-01T00:00:00.000Z');
    return createdDate > cutoffDate;
  };

  // Helper function to format password with extra "!" if domain was created after June 1st, 2025
  const formatPassword = (domain: Domain): string => {
    if (!domain.domain_password) return '';
    const basePassword = domain.domain_password;
    return isCreatedAfterJune2025(domain) ? basePassword + '!' : basePassword;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to format names field
  const formatNames = (names: string | undefined) => {
    if (!names || names.trim() === '') return '-';
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(names);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
      if (typeof parsed === 'object') {
        return Object.values(parsed).join(', ');
      }
      return String(parsed);
    } catch {
      // If not JSON, return as-is but clean up any extra formatting
      return names.replace(/[{}[\]"]/g, '').replace(/,/g, ', ');
    }
  };

  // Helper function to format accounts to create field
  const formatAccountsToCreate = (accounts: string | undefined) => {
    if (!accounts || accounts.trim() === '') return '-';
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(accounts);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
      if (typeof parsed === 'object') {
        return Object.values(parsed).join(', ');
      }
      return String(parsed);
    } catch {
      // If not JSON, return as-is but clean up any extra formatting
      return accounts.replace(/[{}[\]"]/g, '').replace(/,/g, ', ');
    }
  };

  // Helper function to format accounts to cancel field
  const formatAccountsToCancel = (accounts: string | undefined) => {
    if (!accounts || accounts.trim() === '') return '-';
    
    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(accounts);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
      if (typeof parsed === 'object') {
        return Object.values(parsed).join(', ');
      }
      return String(parsed);
    } catch {
      // If not JSON, return as-is but clean up any extra formatting
      return accounts.replace(/[{}[\]"]/g, '').replace(/,/g, ', ');
    }
  };

  const togglePasswordVisibility = (domainId: string) => {
    const newSet = new Set(showPasswords);
    if (newSet.has(domainId)) {
      newSet.delete(domainId);
    } else {
      newSet.add(domainId);
    }
    setShowPasswords(newSet);
  };

  const toggleVerifiedResults = (domainId: string) => {
    const newSet = new Set(expandedResults);
    if (newSet.has(domainId)) {
      newSet.delete(domainId);
    } else {
      newSet.add(domainId);
    }
    setExpandedResults(newSet);
  };

  const copyToClipboard = async (text: string, domainId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedOtpFor(domainId);
      setTimeout(() => setCopiedOtpFor(null), 1500);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const getVerifiedResults = (domain: Domain) => {
    const results = [
      domain.verify_result_1,
      domain.verify_result_2,
      domain.verify_result_3,
      domain.verify_result_4,
      domain.verify_result_5,
    ].filter(result => result && result.trim() !== '');

    return results;
  };

  const hasVerifiedResults = (domain: Domain) => {
    return getVerifiedResults(domain).length > 0;
  };

  // Function to get Cloudflare status for a domain
  const getCloudflareStatus = async (domain: Domain) => {
    const domainId = domain.id;
    
    // If verified_connection is true, return Active immediately
    if (domain.verified_connection) {
      return 'Active';
    }

    // If we already have an Active status stored in created_zone, use it
    if (domain.created_zone === 'Active') {
      return 'Active';
    }

    // Check if we should make an API call based on rate limiting
    if (!apiService.shouldCheckCloudflareStatus(domain)) {
      // Return the existing status or default to Pending
      return domain.created_zone || 'Pending';
    }

    // Check if we've already fetched this status in this session
    if (cloudflareStatuses[domainId]) {
      return cloudflareStatuses[domainId];
    }

    // Prevent duplicate API calls
    if (loadingStatuses.has(domainId)) {
      return 'Processing';
    }

    // Make API call to get current status
    setLoadingStatuses(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(domainId);
      return newSet;
    });
    
    try {
      const response = await apiService.getDomainStatus(domain.domain_name);
      let status = 'Pending'; // Default fallback
      
      if (response.success && response.status) {
        status = response.status;
      }
      
      // Update local state
      setCloudflareStatuses(prev => ({
        ...prev,
        [domainId]: status
      }));

      // Update database with status and timestamp using the new endpoint
      try {
        const updateResult = await apiService.updateDomainStatus(domainId, status);
        if (updateResult.success) {
          console.log(`✓ Updated Cloudflare status for ${domain.domain_name}: ${status}`);
        } else {
          console.warn(`Database update failed for domain ${domain.domain_name}:`, updateResult.message);
        }
      } catch (updateError) {
        console.warn('Failed to update domain status in database:', updateError);
      }

      return status;
    } catch (error) {
      console.error('Error fetching Cloudflare status for', domain.domain_name, ':', error);
      
      // Set a default status on error
      const fallbackStatus = 'Pending';
      setCloudflareStatuses(prev => ({
        ...prev,
        [domainId]: fallbackStatus
      }));
      
      return fallbackStatus;
    } finally {
      // Always remove from loading state
      setLoadingStatuses(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(domainId);
        return newSet;
      });
    }
  };

  // Function to render Cloudflare status
  const renderCloudflareStatus = (domain: Domain) => {
    const domainId = domain.id;

    // If verified_connection is true, show Active
    if (domain.verified_connection) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-500">
          Active
        </span>
      );
    }

    // If we have Active status in created_zone, show it
    if (domain.created_zone === 'active') {
      return (
        <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-500">
          Active
        </span>
      );
    }

    // Check if we're currently loading this domain's status
    if (loadingStatuses.has(domainId)) {
      return (
        <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Processing
        </span>
      );
    }

    // Check if we have a fetched status
    const fetchedStatus = cloudflareStatuses[domainId];
    if (fetchedStatus) {
      if (fetchedStatus === 'Processing') {
        return (
          <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Processing
          </span>
        );
      }
      
      if (fetchedStatus === 'Active') {
        return (
          <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-500">
            Active
          </span>
        );
      }
      
      // For any other fetched status (like "Pending" or "rate_limited"), use amber color
      // Display "rate_limited" as "Pending"
      const displayStatus = fetchedStatus === 'rate_limited' ? 'Pending' : 
                           fetchedStatus === 'pending' ? 'Pending' : fetchedStatus;
      return (
        <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-500">
          {displayStatus}
        </span>
      );
    }

    // Check if we have a stored status in created_zone
    if (domain.created_zone && domain.created_zone !== 'Active') {
      if (domain.created_zone === 'Processing') {
        return (
          <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
            Processing
          </span>
        );
      }
      
      // For other stored statuses, use amber and capitalize
      // Display "rate_limited" as "Pending"
      const displayStatus = domain.created_zone === 'rate_limited' ? 'Pending' :
                           domain.created_zone.toLowerCase() === 'pending' ? 'Pending' : domain.created_zone;
      return (
        <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-500">
          {displayStatus}
        </span>
      );
    }

    // Default: show Pending (API call will be triggered by useEffect)
    return (
      <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-500">
        Pending
      </span>
    );
  };

  // DNS Record Action Handlers
  const handleDkimAction = (domain: Domain) => {
    setSelectedDomain(domain);
    setDkimContent('');
    setShowDkimModal(true);
    setShowActionsModal(false);
  };

  const handleGoogleVerificationAction = (domain: Domain) => {
    setSelectedDomain(domain);
    setGoogleVerificationContent('');
    setShowGoogleVerificationModal(true);
    setShowActionsModal(false);
  };

  const processDkimRecord = async () => {
    if (!selectedDomain || !dkimContent.trim()) {
      alert('Please enter DKIM content');
      return;
    }

    const actionKey = `dkim-${selectedDomain.id}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));

    try {
      const result = await apiService.addDkimRecord(selectedDomain.domain_name, dkimContent.trim());
      
      if (result.success) {
        alert(`DKIM record added successfully for ${selectedDomain.domain_name}`);
        setShowDkimModal(false);
        setDkimContent('');
        setSelectedDomain(null);
        setDkimStatuses(prev => ({ ...prev, [selectedDomain.id]: true }));
      } else {
        alert(`Failed to add DKIM record: ${result.message}`);
      }
    } catch (error) {
      console.error('Error adding DKIM record:', error);
      alert('Failed to add DKIM record. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const processGoogleVerification = async () => {
    if (!selectedDomain || !googleVerificationContent.trim()) {
      alert('Please enter Google verification content');
      return;
    }

    const actionKey = `google-${selectedDomain.id}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));

    try {
      const result = await apiService.addGoogleVerification(selectedDomain.domain_name, googleVerificationContent.trim());
      
      if (result.success) {
        alert(`Google Site Verification record added successfully for ${selectedDomain.domain_name}`);
        setShowGoogleVerificationModal(false);
        setGoogleVerificationContent('');
        setSelectedDomain(null);
        setGoogleVerificationStatuses(prev => ({ ...prev, [selectedDomain.id]: true }));
      } else {
        alert(`Failed to add Google Site Verification record: ${result.message}`);
      }
    } catch (error) {
      console.error('Error adding Google Site Verification record:', error);
      alert('Failed to add Google Site Verification record. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Action handlers for all the new actions
  const handleActionsClick = (domain: Domain) => {
    setSelectedDomain(domain);
    setShowActionsModal(true);
  };

  const handleOAuthUpload = async (domain: Domain) => {
    setShowActionsModal(false);
    
    const actionKey = `oauth-upload-${domain.id}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      // Only process unverified domains
      if (domain.verified_connection) {
        alert(`Domain ${domain.domain_name} is already verified. oAuth Upload only runs for unverified domains.`);
        return;
      }

      // Use backend endpoint (handles Smartlead proxying and platform differences)
      const response = await apiService.oAuthUploadDomain(domain.id);
      
      if (response.success) {
        const { domain: domainName, totalEmails, successCount, failCount, skippedCount = 0 } = response;
        
        let message = `✅ oAuth Upload completed for ${domainName}!\n\n`;
        message += `Summary:\n`;
        message += `• Total emails: ${totalEmails}\n`;
        message += `• Successfully uploaded: ${successCount}\n`;
        message += `• Failed uploads: ${failCount}\n`;
        message += `• Skipped (already connected): ${skippedCount}\n\n`;
        
        if (response.emailResults && response.emailResults.length > 0) {
          message += 'Details:\n';
          response.emailResults.forEach((result: any) => {
            if (result.reason === 'Already connected') {
              message += `⏭️ ${result.email}: Skipped (already connected)\n`;
            } else if (result.success) {
              message += `✅ ${result.email}: Success\n`;
            } else {
              message += `❌ ${result.email}: ${result.error}\n`;
            }
          });
        }
        
        alert(message);
      } else {
        alert(`❌ oAuth Upload failed:\n\n${response.message}\n\nPlease check the domain configuration and try again.`);
      }
    } catch (error) {
      console.error('Error in oAuth upload:', error);
      alert(`❌ Error during oAuth upload: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleSendPassword = (domain: Domain) => {
    setShowActionsModal(false);
    
    // Check for required data
    if (!domain.domain_password) {
      alert(`Cannot send passwords for ${domain.domain_name} - Domain password is missing`);
      return;
    }

    if (!domain.bubble_workspace_id) {
      alert(`Cannot send passwords for ${domain.domain_name} - Workspace ID is missing`);
      return;
    }

    // Check if we're already processing this domain
    const actionKey = `send-password-${domain.id}`;
    if (actionLoading[actionKey]) {
      return;
    }

    // Start the send password process
    sendDomainPassword(domain);
  };

  const sendDomainPassword = async (domain: Domain) => {
    const actionKey = `send-password-${domain.id}`;
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));

    try {
      console.log(`Starting send password for domain ${domain.domain_name}`);
      const result = await apiService.sendDomainPassword(domain.id);
      
      if (result.success) {
        const { totalEmails, successCount, failCount, results } = result;
        
        // Create detailed message
        let message = `Password sent for ${domain.domain_name}!\n\n`;
        message += `Summary: ${successCount}/${totalEmails} emails sent successfully\n\n`;
        
        if (results && results.length > 0) {
          message += 'Results:\n';
          results.forEach((result: any) => {
            if (result.success) {
              message += `✅ ${result.email}: Success\n`;
            } else {
              message += `❌ ${result.email}: ${result.error}\n`;
            }
          });
        }
        
        alert(message);
        console.log(`✅ Send password completed for ${domain.domain_name}`);
      } else {
        alert(`Send password failed for ${domain.domain_name}: ${result.message}`);
      }
    } catch (error) {
      console.error('Error during send password:', error);
      alert(`Send password failed for ${domain.domain_name} due to an error. Please try again.`);
    } finally {
      // Remove from loading state
      setActionLoading(prev => {
        const newState = { ...prev };
        delete newState[actionKey];
        return newState;
      });
    }
  };

  const handleVerifyConnection = (domain: Domain) => {
    setShowActionsModal(false);
    
    if (domainVerificationLoading.has(domain.id)) {
      return; // Already verifying this domain
    }

    // Add domain to loading set
    setDomainVerificationLoading(prev => new Set(Array.from(prev).concat(domain.id)));

    // Start verification
    verifySingleDomain(domain);
  };

  const verifySingleDomain = async (domain: Domain) => {
    try {
      console.log(`Starting verification for domain ${domain.domain_name}`);
      const result = await apiService.verifySingleDomain(domain.id);
      
      if (result.success) {
        const { totalEmails, connectedEmails, allEmailsConnected } = result;
        
        alert(`Verification completed for ${domain.domain_name}!\n\nResult: ${allEmailsConnected ? 'Domain Verified ✅' : 'Partial Connection'}\nConnected: ${connectedEmails}/${totalEmails} emails`);
        
        // The domain will be refreshed from parent component if verified
        if (allEmailsConnected) {
          console.log(`Domain ${domain.domain_name} is now verified`);
        }
      } else {
        alert(`Verification failed for ${domain.domain_name}: ${result.message}`);
      }
    } catch (error) {
      console.error('Error during single domain verification:', error);
      alert(`Verification failed for ${domain.domain_name} due to an error. Please try again.`);
    } finally {
      // Remove domain from loading set
      setDomainVerificationLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(domain.id);
        return newSet;
      });
    }
  };

  const handleMarkVerified = (domain: Domain) => {
    setShowActionsModal(false);
    // TODO: Implement Mark Verified functionality
    alert(`Mark Verified for ${domain.domain_name} - Feature coming soon!`);
  };

  const handleCancelDomain = (domain: Domain) => {
    setShowActionsModal(false);
    // TODO: Implement Cancel Domain functionality
    if (window.confirm(`Are you sure you want to cancel domain ${domain.domain_name}?`)) {
      alert(`Cancel Domain for ${domain.domain_name} - Feature coming soon!`);
    }
  };

  // Effect to trigger API calls for domains that need status checks
  React.useEffect(() => {
    if (!domains || domains.length === 0) return;

    const checkDomainStatuses = async () => {
      // Filter domains that need status checks using our rate limiting logic
      const domainsToCheck = domains.filter(domain => {
        const domainId = domain.id;
        
        // Skip if we're already loading this domain or have fetched in this session
        if (loadingStatuses.has(domainId) || cloudflareStatuses[domainId]) {
          return false;
        }
        
        // Use the API service helper to determine if we should check
        return apiService.shouldCheckCloudflareStatus(domain);
      });

      console.log(`Checking Cloudflare status for ${domainsToCheck.length} domains (${domains.length} total, rate limiting applied)`);

      // Process domains that need checking
      for (const domain of domainsToCheck) {
        try {
          await getCloudflareStatus(domain);
          // Small delay between domains to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
          console.error(`Failed to get status for domain ${domain.domain_name}:`, error);
        }
      }
    };

    checkDomainStatuses();
  }, [domains]); // Only depend on domains array to prevent infinite re-renders

  // Clear selection when domains list changes
  React.useEffect(() => {
    setSelectedDomains(new Set());
  }, [domains]);

  // Load manager display names for assigned domains
  React.useEffect(() => {
    const loadManagerNames = async () => {
      const uniqueUsernames = Array.from(new Set(
        domains
          .filter(domain => domain.assigned_va)
          .map(domain => domain.assigned_va!)
      ));

      for (const username of uniqueUsernames) {
        if (!managerNames[username]) {
          try {
            const result = await apiService.getManagerByUsername(username);
            if (result.success && result.data) {
              setManagerNames(prev => ({
                ...prev,
                [username]: result.data!.firstName
              }));
            }
          } catch (error) {
            console.error(`Error loading manager name for ${username}:`, error);
            setManagerNames(prev => ({
              ...prev,
              [username]: username // Fallback to username
            }));
          }
        }
      }
    };

    if (domains.length > 0) {
      loadManagerNames();
    }
  }, [domains, managerNames]);

  // Claim domain function
  const handleClaimDomain = async (domain: Domain) => {
    if (!currentUser || !currentUser.username) {
      alert('You must be logged in as a manager to claim domains');
      return;
    }

    if (domain.assigned_va && domain.assigned_va !== currentUser.username) {
      alert(`This domain is already claimed by ${managerNames[domain.assigned_va] || domain.assigned_va}`);
      return;
    }

    setClaimingDomains(prev => new Set(prev).add(domain.id));

    try {
      const result = await apiService.claimDomain(domain.id, currentUser.username);
      
      if (result.success) {
        alert(`Domain ${domain.domain_name} has been claimed successfully!`);
        // Update the domain in place to reflect the change
        domain.assigned_va = currentUser.username;
        domain.claimed_at = new Date().toISOString();
        
        // Load the manager's display name
        setManagerNames(prev => ({
          ...prev,
          [currentUser.username]: currentUser.firstName || currentUser.username
        }));
        
        // Force a re-render
        window.location.reload();
      } else {
        alert(`Failed to claim domain: ${result.message}`);
      }
    } catch (error) {
      console.error('Error claiming domain:', error);
      alert('Failed to claim domain. Please try again.');
    } finally {
      setClaimingDomains(prev => {
        const newSet = new Set(prev);
        newSet.delete(domain.id);
        return newSet;
      });
    }
  };

  // Unclaim domain function
  const handleUnclaimDomain = async (domain: Domain) => {
    if (!currentUser || !currentUser.username) {
      alert('You must be logged in as a manager to unclaim domains');
      return;
    }

    if (domain.assigned_va !== currentUser.username && currentUser.userType !== 'superadmin') {
      alert('You can only unclaim domains that you have claimed');
      return;
    }

    if (!window.confirm(`Are you sure you want to unclaim domain ${domain.domain_name}?`)) {
      return;
    }

    setClaimingDomains(prev => new Set(prev).add(domain.id));

    try {
      const result = await apiService.unclaimDomain(domain.id);
      
      if (result.success) {
        alert(`Domain ${domain.domain_name} has been unclaimed successfully!`);
        // Update the domain in place to reflect the change
        domain.assigned_va = undefined;
        domain.claimed_at = undefined;
        
        // Force a re-render
        window.location.reload();
      } else {
        alert(`Failed to unclaim domain: ${result.message}`);
      }
    } catch (error) {
      console.error('Error unclaiming domain:', error);
      alert('Failed to unclaim domain. Please try again.');
    } finally {
      setClaimingDomains(prev => {
        const newSet = new Set(prev);
        newSet.delete(domain.id);
        return newSet;
      });
    }
  };

  // Render claim status and buttons
  const renderClaimStatus = (domain: Domain) => {
    const isClaimingInProgress = claimingDomains.has(domain.id);
    const isManager = currentUser?.userType === 'manager';
    const isSuperAdmin = currentUser?.userType === 'superadmin';
    const canInteract = isManager || isSuperAdmin;

    if (domain.assigned_va) {
      const displayName = managerNames[domain.assigned_va] || domain.assigned_va;
      const isOwnClaim = domain.assigned_va === currentUser?.username;
      const canUnclaim = isOwnClaim || isSuperAdmin;

      return (
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-500">
            Claimed by {displayName}
          </span>
          {canInteract && canUnclaim && (
            <button
              onClick={() => handleUnclaimDomain(domain)}
              disabled={isClaimingInProgress}
              className="p-1 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
              title="Unclaim domain"
            >
              {isClaimingInProgress ? (
                <Loader2 className="h-3 w-3 animate-spin text-red-500" />
              ) : (
                <UserMinus className="h-3 w-3 text-red-500" />
              )}
            </button>
          )}
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-2">
          <span className="inline-flex items-center rounded-full bg-gray-500/10 px-2 py-0.5 text-xs font-medium text-gray-500">
            Unassigned
          </span>
          {canInteract && (
            <button
              onClick={() => handleClaimDomain(domain)}
              disabled={isClaimingInProgress}
              className="p-1 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
              title="Claim domain"
            >
              {isClaimingInProgress ? (
                <Loader2 className="h-3 w-3 animate-spin text-green-500" />
              ) : (
                <UserPlus className="h-3 w-3 text-green-500" />
              )}
            </button>
          )}
        </div>
      );
    }
  };

  // Bulk selection helper functions
  const handleSelectDomain = (domainId: string) => {
    setSelectedDomains(prev => {
      const newSet = new Set(prev);
      if (newSet.has(domainId)) {
        newSet.delete(domainId);
      } else {
        newSet.add(domainId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const unclaimedDomains = domains.filter(domain => !domain.assigned_va);
    if (selectedDomains.size === unclaimedDomains.length) {
      // If all unclaimed domains are selected, deselect all
      setSelectedDomains(new Set());
    } else {
      // Select all unclaimed domains
      setSelectedDomains(new Set(unclaimedDomains.map(domain => domain.id)));
    }
  };

  const clearSelection = () => {
    setSelectedDomains(new Set());
  };

  const handleBulkClaim = async () => {
    if (!currentUser || !currentUser.username) {
      alert('You must be logged in as a manager to claim domains');
      return;
    }

    if (selectedDomains.size === 0) {
      alert('Please select domains to claim');
      return;
    }

    const domainsToProcess = Array.from(selectedDomains);
    const confirmMessage = `Are you sure you want to claim ${domainsToProcess.length} domain${domainsToProcess.length !== 1 ? 's' : ''}?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkClaimingInProgress(true);

    try {
      let successCount = 0;
      let failCount = 0;
      const errors: string[] = [];

      for (const domainId of domainsToProcess) {
        try {
          const result = await apiService.claimDomain(domainId, currentUser.username);
          if (result.success) {
            successCount++;
            // Update the domain in place
            const domain = domains.find(d => d.id === domainId);
            if (domain) {
              domain.assigned_va = currentUser.username;
              domain.claimed_at = new Date().toISOString();
            }
          } else {
            failCount++;
            errors.push(`${domainId}: ${result.message}`);
          }
        } catch (error) {
          failCount++;
          errors.push(`${domainId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      // Load the manager's display name
      setManagerNames(prev => ({
        ...prev,
        [currentUser.username]: currentUser.firstName || currentUser.username
      }));

      // Clear selection
      clearSelection();

      // Show results
      let message = `Bulk claim completed!\n\nSuccessful: ${successCount}\nFailed: ${failCount}`;
      if (errors.length > 0 && errors.length <= 5) {
        message += '\n\nErrors:\n' + errors.join('\n');
      } else if (errors.length > 5) {
        message += `\n\nFirst 5 errors:\n${errors.slice(0, 5).join('\n')}\n... and ${errors.length - 5} more`;
      }

      alert(message);

      // Force a re-render
      if (successCount > 0) {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error in bulk claim:', error);
      alert('Failed to process bulk claim. Please try again.');
    } finally {
      setBulkClaimingInProgress(false);
    }
  };

  // Filter domains for bulk selection (only show unclaimed domains for selection)
  const getSelectableDomains = () => {
    return domains.filter(domain => !domain.assigned_va);
  };

  const unclaimedDomains = getSelectableDomains();
  const allUnclaimedSelected = unclaimedDomains.length > 0 && selectedDomains.size === unclaimedDomains.length;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-12 text-destructive">{error}</div>;
  }

  if (domains.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No domains found for this user.</p>
      </div>
    );
  }

  return (
    <div className="card-modern overflow-hidden">
      {/* Bulk Actions Toolbar */}
      {selectedDomains.size > 0 && (currentUser?.userType === 'manager' || currentUser?.userType === 'superadmin') && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedDomains.size} domain{selectedDomains.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline"
              >
                Clear selection
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkClaim}
                disabled={bulkClaimingInProgress}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                {bulkClaimingInProgress ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Claim Selected
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              {/* Checkbox Column */}
              {(currentUser?.userType === 'manager' || currentUser?.userType === 'superadmin') && (
                <th scope="col" className="px-3 py-3 text-left text-sm font-semibold w-10">
                  <input
                    type="checkbox"
                    checked={allUnclaimedSelected}
                    onChange={handleSelectAll}
                    disabled={unclaimedDomains.length === 0}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                    title={unclaimedDomains.length === 0 ? 'No unclaimed domains to select' : 'Select all unclaimed domains'}
                  />
                </th>
              )}
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[150px]">
                Assigned VA
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Cloudflare Status
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Account Type
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[100px]">
                Platform
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[150px]">
                Workspace ID
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[180px]">
                Domain
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Names
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Accounts to Create
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Password
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[140px]">
                OTP Secret
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[100px]">
                Google Verification
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[80px]">
                DKIM Added
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Verified Connection
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[140px]">
                Verified Results
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Order Date
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Accounts to Cancel
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[120px]">
                Canceled Date
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[140px]">
                Actions
              </th>
              <th scope="col" className="px-3 py-3 text-left text-sm font-semibold min-w-[180px]">
                Redirect URL
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {domains.map((domain) => (
              <React.Fragment key={domain.id}>
                <tr className="hover:bg-muted/30 transition-colors">
                  {/* Checkbox Column */}
                  {(currentUser?.userType === 'manager' || currentUser?.userType === 'superadmin') && (
                    <td className="px-3 py-3 text-sm">
                      <input
                        type="checkbox"
                        checked={selectedDomains.has(domain.id)}
                        onChange={() => handleSelectDomain(domain.id)}
                        disabled={!!domain.assigned_va}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                        title={!!domain.assigned_va ? 'Domain is already claimed' : 'Select this domain'}
                      />
                    </td>
                  )}
                  {/* Assigned VA */}
                  <td className="px-3 py-3 text-sm">
                    {renderClaimStatus(domain)}
                  </td>

                  {/* Cloudflare Status */}
                  <td className="px-3 py-3 text-sm">
                    {renderCloudflareStatus(domain)}
                  </td>

                  {/* Account Type */}
                  <td className="px-3 py-3 text-sm">
                    <span className="inline-flex items-center rounded-full bg-purple-500/10 px-2 py-0.5 text-xs font-medium text-purple-500">
                      {domain.account_type || 'N/A'}
                    </span>
                  </td>

                  {/* Platform */}
                  <td className="px-3 py-3 text-sm">
                    <span className="inline-flex items-center rounded-full bg-blue-500/10 px-2 py-0.5 text-xs font-medium text-blue-500">
                      {domain.platform || 'N/A'}
                    </span>
                  </td>

                  {/* Workspace ID */}
                  <td className="px-3 py-3 text-sm font-mono">
                    <div className="break-all">
                      {domain.bubble_workspace_id || '-'}
                    </div>
                  </td>

                  {/* Domain */}
                  <td className="px-3 py-3 text-sm font-medium">
                    <div className="break-all">
                      {domain.domain_name}
                    </div>
                  </td>

                  {/* Names */}
                  <td className="px-3 py-3 text-sm">
                    <div className="break-words">
                      {formatNames(domain.names)}
                    </div>
                  </td>

                  {/* Accounts to Create */}
                  <td className="px-3 py-3 text-sm">
                    <div className="break-words">
                      {formatAccountsToCreate(domain.accounts_to_create)}
                    </div>
                  </td>

                  {/* Password */}
                  <td className="px-3 py-3 text-sm">
                    {domain.domain_password ? (
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-xs">
                          {showPasswords.has(domain.id) 
                            ? formatPassword(domain) 
                            : '••••••••'
                          }
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(domain.id)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                          title={showPasswords.has(domain.id) ? 'Hide password' : 'Show password'}
                        >
                          {showPasswords.has(domain.id) ? (
                            <EyeOff className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <Eye className="h-3 w-3 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>

                  {/* OTP Secret (masked, copy on click). Show only if stored. */}
                  <td className="px-3 py-3 text-sm">
                    {domain.platform?.toLowerCase() === 'smartlead' && (domain as any).otp_secret ? (
                      <button
                        onClick={() => copyToClipboard((domain as any).otp_secret as string, domain.id)}
                        className="inline-flex items-center space-x-2 px-2 py-1 rounded hover:bg-muted transition-colors"
                        title="Click to copy OTP Secret"
                      >
                        <span className="font-mono text-xs">••••••••</span>
                        <Copy className="h-3 w-3 text-muted-foreground" />
                        {copiedOtpFor === domain.id && (
                          <span className="text-xs text-green-600 ml-1">Copied</span>
                        )}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>

                  {/* Google Verification */}
                  <td className="px-3 py-3 text-sm">
                    <div className="flex justify-center">
                      {(googleVerificationStatuses[domain.id] !== undefined ? googleVerificationStatuses[domain.id] : (domain.site_verification_record_added ? true : false)) ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </td>

                  {/* DKIM Added */}
                  <td className="px-3 py-3 text-sm">
                    <div className="flex justify-center">
                      {(dkimStatuses[domain.id] !== undefined ? dkimStatuses[domain.id] : domain.dkim_added) ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </td>

                  {/* Verified Connection */}
                  <td className="px-3 py-3 text-sm">
                    <div className="flex items-center space-x-2">
                      {domain.verified_connection ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-600 text-xs font-medium">Verified</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-amber-500" />
                          <span className="text-amber-600 text-xs font-medium">Pending</span>
                        </>
                      )}
                    </div>
                  </td>

                  {/* Verified Results */}
                  <td className="px-3 py-3 text-sm">
                    {hasVerifiedResults(domain) ? (
                      <button
                        onClick={() => toggleVerifiedResults(domain.id)}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium underline"
                      >
                        {expandedResults.has(domain.id) ? 'Hide Results' : `View Results (${getVerifiedResults(domain).length})`}
                      </button>
                    ) : (
                      <span className="text-muted-foreground text-xs">No results</span>
                    )}
                  </td>

                  {/* Order Date */}
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {formatDate(domain.created_at)}
                  </td>

                  {/* Accounts to Cancel */}
                  <td className="px-3 py-3 text-sm">
                    <div className="break-words">
                      {formatAccountsToCancel(domain.accounts_to_cancel)}
                    </div>
                  </td>

                  {/* Canceled Date */}
                  <td className="px-3 py-3 text-sm text-muted-foreground">
                    {domain.canceled_date ? formatDate(domain.canceled_date) : '-'}
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-3 text-sm">
                    <button
                      onClick={() => handleActionsClick(domain)}
                      className="flex items-center space-x-1 px-2 py-1 text-xs text-muted-foreground hover:text-foreground border border-border rounded hover:bg-muted transition-colors"
                    >
                      <MoreVertical className="h-3 w-3" />
                      <span>Actions</span>
                    </button>
                  </td>

                  {/* Redirect URL */}
                  <td className="px-3 py-3 text-sm">
                    {domain.redirect_url_link ? (
                      <a
                        href={domain.redirect_url_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline break-all"
                        title={domain.redirect_url_link}
                      >
                        Open Redirect
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>

                {/* Expanded Verified Results Row */}
                {expandedResults.has(domain.id) && hasVerifiedResults(domain) && (
                  <tr className="bg-muted/20">
                    <td colSpan={18} className="px-3 py-3">
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">Verified Results:</h4>
                        <div className="space-y-1">
                          {getVerifiedResults(domain).map((result, index) => (
                            <div key={index} className="flex items-start space-x-2">
                              <span className="text-xs font-mono bg-muted px-2 py-1 rounded min-w-[60px] text-center">
                                Result {index + 1}
                              </span>
                              <span className="text-xs text-muted-foreground break-all">
                                {result}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* DKIM Modal */}
      {showDkimModal && selectedDomain && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-card rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold">Add DKIM Record</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDomain.domain_name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDkimModal(false);
                  setSelectedDomain(null);
                  setDkimContent('');
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 p-6 space-y-4 overflow-y-auto">
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  DKIM Content
                </label>
                <textarea
                  className="input-modern w-full h-32 px-4 py-3 text-sm border-2 focus:border-primary"
                  placeholder="Enter DKIM record content..."
                  value={dkimContent}
                  onChange={(e) => setDkimContent(e.target.value)}
                  style={{ resize: 'vertical', minHeight: '120px' }}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Enter the DKIM record content that will be added to the DNS for this domain.
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
              <button
                onClick={() => {
                  setShowDkimModal(false);
                  setSelectedDomain(null);
                  setDkimContent('');
                }}
                className="px-6 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={processDkimRecord}
                disabled={!dkimContent.trim() || actionLoading[`dkim-${selectedDomain.id}`]}
                className="btn-primary px-6 py-2 text-sm font-medium rounded-lg disabled:opacity-50 min-w-[120px]"
              >
                {actionLoading[`dkim-${selectedDomain.id}`] ? (
                  <div className="inline-flex items-center justify-center">
                    <Loader2 className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Adding...
                  </div>
                ) : (
                  'Add DKIM Record'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Google Verification Modal */}
      {showGoogleVerificationModal && selectedDomain && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-card rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold">Add Google Site Verification</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedDomain.domain_name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowGoogleVerificationModal(false);
                  setSelectedDomain(null);
                  setGoogleVerificationContent('');
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 p-6 space-y-4 overflow-y-auto">
              <div className="space-y-3">
                <label className="text-sm font-medium text-foreground">
                  Google Verification Content
                </label>
                <input
                  type="text"
                  className="input-modern w-full px-4 py-3 text-sm border-2 focus:border-primary"
                  placeholder="google-site-verification=your-verification-code"
                  value={googleVerificationContent}
                  onChange={(e) => setGoogleVerificationContent(e.target.value)}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Enter the Google Site Verification content (e.g., google-site-verification=abcd1234...)
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
              <button
                onClick={() => {
                  setShowGoogleVerificationModal(false);
                  setSelectedDomain(null);
                  setGoogleVerificationContent('');
                }}
                className="px-6 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={processGoogleVerification}
                disabled={!googleVerificationContent.trim() || actionLoading[`google-${selectedDomain.id}`]}
                className="btn-primary px-6 py-2 text-sm font-medium rounded-lg disabled:opacity-50 min-w-[160px]"
              >
                {actionLoading[`google-${selectedDomain.id}`] ? (
                  <div className="inline-flex items-center justify-center">
                    <Loader2 className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Adding...
                  </div>
                ) : (
                  'Add Verification Record'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Actions Modal */}
      {showActionsModal && selectedDomain && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-card rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold">Actions for {selectedDomain.domain_name}</h2>
              </div>
              <button
                onClick={() => {
                  setShowActionsModal(false);
                  setSelectedDomain(null);
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="flex-1 p-6 space-y-2 overflow-y-auto">
              <button
                onClick={() => handleGoogleVerificationAction(selectedDomain)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3"
              >
                <Search className="h-4 w-4 text-blue-500" />
                <span>Add Google Verification</span>
              </button>
              
              <button
                onClick={() => handleDkimAction(selectedDomain)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3"
              >
                <Shield className="h-4 w-4 text-green-500" />
                <span>Add DKIM</span>
              </button>
              
              <button
                onClick={() => handleOAuthUpload(selectedDomain)}
                disabled={actionLoading[`oauth-upload-${selectedDomain.id}`]}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3 disabled:opacity-50"
              >
                {actionLoading[`oauth-upload-${selectedDomain.id}`] ? (
                  <>
                    <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
                    <span>Processing oAuth Upload...</span>
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 text-purple-500" />
                    <span>oAuth Upload</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleSendPassword(selectedDomain)}
                disabled={actionLoading[`send-password-${selectedDomain.id}`]}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3 disabled:opacity-50"
              >
                {actionLoading[`send-password-${selectedDomain.id}`] ? (
                  <>
                    <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
                    <span>Sending Password...</span>
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 text-amber-500" />
                    <span>Send Password</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleVerifyConnection(selectedDomain)}
                disabled={domainVerificationLoading.has(selectedDomain.id)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3 disabled:opacity-50"
              >
                {domainVerificationLoading.has(selectedDomain.id) ? (
                  <>
                    <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />
                    <span>Verifying Connection...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 text-blue-500" />
                    <span>Verify Connection</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleMarkVerified(selectedDomain)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3"
              >
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Mark Verified</span>
              </button>
              
              <button
                onClick={() => handleCancelDomain(selectedDomain)}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors flex items-center space-x-3"
              >
                <XCircle className="h-4 w-4 text-red-500" />
                <span>Cancel Domain</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}; 